import { BridgeComponent } from "@hotwired/hotwire-native-bridge"

// Bridge component for the offline write queue (CONEK-4571 P6 Task 6).
//
// Scope — deliberately narrow: this controller ONLY intercepts the forms it
// is explicitly mounted on:
//   - `packs/pipeline/app/views/mobile/tasks/_form.html.erb`  (POST /mobile/tasks)
//   - `packs/telephony/app/views/mobile/telephony/calls/_form.html.erb`
//     (POST /mobile/telephony/calls)
//   - `packs/inspections/app/views/mobile/inspections/pages/show.html.erb`
//     (PATCH /mobile/inspections/:id/pages/:id, the pager answers — CONEK-8857;
//     sent as POST with Rails' `_method=patch` in the body)
// It is NOT a generic "intercept every form" controller — those views are the
// only ones that mount `data-controller="bridge--offline-queue"`. Each target
// endpoint includes `Mobile::Idempotent`, which acknowledges a replay.
// Any other form submission on the mobile shell is completely unaffected.
//
// CONEK-7256 — one other controller now sends to the SAME native
// `offline-queue` component without going through this one:
// `inspections--photo-grid` captures an inspection photo as a base64 data URL
// (a File cannot survive the `URLSearchParams` serialisation below). It talks to
// `window.HotwireNative.web` directly because it must keep loading in plain
// browsers, which `BridgeComponent.shouldLoad` would prevent. The native side is
// unchanged and payload-agnostic; if you change the enqueue wire shape here,
// change it there too.
//
// Gating — independent of `mobile.rep.push` (a different flag, no shared
// logic): mounted only when BOTH are true:
//   1. the `mobile-rep-offline` meta tag is present (server-side
//      `mobile.rep.offline` Flipper flag, resolved via the mobile layout —
//      mirrors the `mobile-rep-push` meta-tag pattern from Task 5 exactly)
//   2. the device is actually offline right now, checked at submit time via
//      `navigator.onLine === false` (network-level signal) OR (when running
//      inside the Turbo Native shell) the native bridge's own connectivity
//      report — see `nativeReportsOffline()`.
//
// When both hold and the form is submitted, we prevent the normal HTTP
// submit, capture `{method, url, body, content_type, csrf_token}` from the
// form itself, and hand it to native via the `enqueue` bridge message. The
// native side (`OfflineQueueBridgeComponent` on Android) persists it in the
// Room-backed queue and schedules `OfflineFlushWorker` to replay it once
// WorkManager sees connectivity return (`Constraints(NetworkType.CONNECTED)`).
//
// Once native replies `queued`, the rep is told (CONEK-8280): the form stays
// on screen, so without a word an offline Save looked like a dead button and
// reps tapped it again. `showQueuedToast()` clones the server-rendered
// "Saved offline" toast (`shared/_mobile_offline_queued_toast`, the
// `queuedToast` target) into #mobile-toast.
//
// Every enqueue is a separate record on replay, so a second tap must not
// queue the same write again: a submit whose form data matches the write the
// queue already holds (or is still persisting) only shows the toast again.
// Changed data is a new write and is queued. The button is never disabled.
//
// Graceful degradation: if the meta tag is absent, if there's no native
// bridge (plain mobile browser), or if the device is online, every submit
// goes through as a normal Rails form POST — completely unchanged behavior.
// Native replies `queued` as soon as its local Room insert lands, within
// milliseconds. Some failures (detached fragment, a failed insert) send no
// reply at all; a tap on a write still unanswered after this long is sent
// again rather than ignored, so a lost enqueue never turns Save into a dead
// button. It is far above any real insert time, so a slow reply is not taken
// for a lost one.
const PENDING_TIMEOUT_MS = 15000

// The queued body is form-urlencoded, which carries text only. Passing the
// whole FormData to URLSearchParams turns every file input into the string
// "[object File]" — even an empty one. CONEK-8279: the task form's Lexxy
// editor brings `task[description]_insert_image`, which Rack reads as
// `task[description][_insert_image]`; next to the `task[description]` string
// that is a type clash, so every replayed task answered 400 (500 on
// conek.dev, where the error page re-parses the same params). Files are
// never replayable this way, so they are left out.
export function serializeTextFields(formData) {
  const params = new URLSearchParams()
  for (const [name, value] of formData) {
    if (typeof value === "string") params.append(name, value)
  }
  return params.toString()
}

export default class extends BridgeComponent {
  static component = "offline-queue"

  static targets = ["form", "queuedToast"]

  // Tracked per form body, not in one slot: a rep can save, edit and save
  // again before the first reply arrives, and each reply must settle its own
  // write, never overwrite the other's state.
  queuedBodies = new Set()
  pendingSince = new Map()

  connect() {
    if (!this.enabled()) return

    super.connect()
    if (this.hasFormTarget) {
      this.formTarget.addEventListener("submit", this.handleSubmit)
    }
  }

  disconnect() {
    if (this.hasFormTarget) {
      this.formTarget.removeEventListener("submit", this.handleSubmit)
    }
    super.disconnect()
  }

  enabled() {
    return (
      document.querySelector('meta[name="mobile-rep-offline"]')?.content ===
      "true"
    )
  }

  isOffline() {
    return navigator.onLine === false || this.nativeReportsOffline()
  }

  // Placeholder hook for a future native "are we offline" signal richer than
  // `navigator.onLine` (e.g. captive-portal detection). Today the bridge has
  // no such round-trip, so this always defers to `navigator.onLine`.
  nativeReportsOffline() {
    return false
  }

  // Arrow form so `this` stays bound through the event listener.
  handleSubmit = (event) => {
    if (!this.isOffline()) return

    event.preventDefault()

    const form = this.formTarget
    const body = serializeTextFields(new FormData(form))

    if (this.queuedBodies.has(body)) {
      this.showQueuedToast()
      // Still queued, so say so again: listeners that reacted to this tap's
      // submit (the inspection pager marks its buttons busy) settle on it.
      this.dispatch("queued", { target: form })
      return
    }
    const sentAt = this.pendingSince.get(body)
    if (sentAt !== undefined && Date.now() - sentAt < PENDING_TIMEOUT_MS) return

    const csrfToken =
      document.querySelector('meta[name="csrf-token"]')?.content ?? ""

    this.pendingSince.set(body, Date.now())
    this.send(
      "enqueue",
      {
        method: (form.getAttribute("method") || "post").toUpperCase(),
        url: form.getAttribute("action") || window.location.pathname,
        body,
        content_type: "application/x-www-form-urlencoded",
        csrf_token: csrfToken,
      },
      (message) => this.handleEnqueueReply(message, form, body),
    )
  }

  handleEnqueueReply(message, form, body) {
    const data =
      message?.data !== null &&
      message?.data !== undefined &&
      typeof message.data === "object"
        ? message.data
        : message

    this.pendingSince.delete(body)

    if (data?.queued) {
      this.queuedBodies.add(body)
      this.showQueuedToast()
      this.dispatch("queued", { target: form })
    }
  }

  // One toast at a time: a repeat tap replaces the one still showing rather
  // than stacking copies of the same message.
  showQueuedToast() {
    const container = document.getElementById("mobile-toast")
    const toast =
      this.hasQueuedToastTarget &&
      this.queuedToastTarget.content.firstElementChild?.cloneNode(true)
    if (!container || !toast) return

    this.shownToast?.remove()
    // The clone carries the id the template was rendered with; give each copy
    // its own, random like ToastComponent's server-side SecureRandom.hex.
    toast.id = `toast-offline-${Math.random().toString(36).slice(2, 10)}`
    container.append(toast)
    this.shownToast = toast
  }
}
